
import Vue from 'vue'
import { mapGetters } from 'vuex'
import moment from 'moment'
import stepByStepValidate from '@whitelist/wl-old-library/services/_helpers/stepByStepValidate'
import services from '@/constants/services'
import { LevelName } from './types/index'

export default Vue.extend({
  name: 'emailGAConfirmPopupChunk',
  props: {
    slotData: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      step: 1,
      form: {
        fields: {
          emailCode: {
            valid: false,
            codeIsSent: false,
            codeSentBefore: false,
            retryTimeout: 60,
            retries: 3,
            letRetry: false,
            value: '',
            rules: [
              (v: string) => Boolean(v) || this.$t('common.errors.required'),
              (v: string) => {
                const vm = this as any
                return v.length === vm.SERVICES.CODE_LENGTH || vm.$t('common.errors.codeMustConsist', { length: vm.SERVICES.CODE_LENGTH })
              }
            ],
            sending: false,
            isLoading: false,
            responseErrors: []
          },
          GACode: {
            valid: false,
            value: '',
            rules: [
              (v: string) => Boolean(v) || this.$t('common.errors.required'),
              (v: string) => {
                const vm = this as any
                return v.length === vm.SERVICES.CODE_LENGTH || vm.$t('popups.numberCode', { length: vm.SERVICES.CODE_LENGTH })
              }
            ],
            isLoading: false,
            responseErrors: []
          }
        }
      }
    }
  },
  computed: {
    ...mapGetters('user', ['getUserData']),
    SERVICES () {
      return services
    }
  },
  watch: {
    'form.fields.emailCode.retryTimeout' (v) {
      const vm = this as any
      if (v > 0) {
        setTimeout(() => {
          vm.form.fields.emailCode.retryTimeout = v - 1
        }, 1000)
      } else vm.form.fields.emailCode.letRetry = true
    },
    'form.fields.emailCode.valid' (v) {
      const vm = this as any
      if (v && vm.form.fields.emailCode.value.length > 0) this.validateFirstStep()
    },
    'form.fields.GACode.valid' (v) {
      const vm = this as any
      if (v && vm.form.fields.GACode.value.length > 0) this.validateSecondStep()
    },
    'form.fields.emailCode.value' () {
      this.form.fields.emailCode.responseErrors = []
    },
    'form.fields.GACode.value' () {
      this.form.fields.GACode.responseErrors = []
    }
  },
  created () {
    this.sendEmail()
  },
  methods: {
    hasntAccessHandler () {
      const vm = this as any
      this.$store.commit('popupModule/setClosed')
      const hasCurrentMethod = this.getUserData.currentAccessRepairMethods.includes('ga')
      if (hasCurrentMethod) {
        this.$store.dispatch('alertsBus/openAlert', {
          title: vm.$t('alerts.titles.info'),
          type: 'info',
          autoDelete: true,
          content: vm.$t('alerts.content.gotResetRequest')
        })
        return
      }
      const isBasic = this.getUserData.verificationRights.levelName === LevelName.basic
      if (isBasic) {
        vm.$store.dispatch('popupModule/openPopup', {
          type: 'confirmPopup',
          letDefaultClose: false,
          title: vm.$t('popups.titles.repairGA'),
          content: vm.$t('popups.content.toRepairGA'),
          actions: [
            {
              title: vm.$t('popups.actions.toSection'),
              isAccent: true,
              callback () {
                vm.$store.commit('popupModule/setClosed')
                vm.slotData.changeKycAccordionStatus(true)
              }
            },
            {
              title: vm.$t('popups.actions.later'),
              isAccent: false,
              callback () {
                vm.$store.commit('popupModule/setClosed')
              }
            }
          ]
        })
        return
      }
      this.$router.push({ name: 'AccessRepair', query: { secondFactor: 'ga', an3K8QkkJb3Mzz: this.slotData.sessionId } })
    },
    async validateFirstStep (): Promise<void> {
      const vm = this as any
      vm.form.fields.emailCode.responseErrors = []
      vm.form.fields.emailCode.isLoading = true
      setTimeout(async () => {
        const response = await stepByStepValidate({
          endpointName: vm.slotData.endpointName,
          name: vm.slotData.name,
          data: {
            sessionId: vm.slotData.sessionId,
            payload: { email: vm.form.fields.emailCode.value }
          },
          steps: vm.slotData.steps
        })
        vm.form.fields.emailCode.isLoading = false
        response.success ? vm.step = 2 : vm.form.fields.emailCode.responseErrors = response.errors
      }, 1000)
    },
    async validateSecondStep (): Promise<void> {
      const vm = this as any
      vm.form.fields.GACode.responseErrors = []
      vm.form.fields.GACode.isLoading = true

      setTimeout(async () => {
        const response = await stepByStepValidate({
          endpointName: vm.slotData.endpointName,
          name: vm.slotData.name,
          data: {
            sessionId: vm.slotData.sessionId,
            payload: { ga: vm.form.fields.GACode.value }
          },
          steps: vm.slotData.steps
        })
        vm.form.fields.GACode.isLoading = false

        response.success ? vm.$emit('onSuccess') : vm.form.fields.GACode.responseErrors = response.errors
      }, 1000)
    },
    handleCancel () {
      const vm = this as any
      vm.$store.commit('popupModule/setClosed')
      vm.$emit('onCancel')
    },
    async sendEmail (): Promise<void> {
      const vm = this as any
      vm.form.fields.emailCode.codeSentBefore = false
      vm.form.fields.emailCode.sending = true

      const sendCodeResetPass = this.slotData.sendCodeMethod === 'passwordResetSendCode'
      const payload = sendCodeResetPass ? { sessionId: vm.slotData.sessionId, payload: { send: 'email' } } : vm.slotData.codes.email.type

      const commonServiceMethod = vm.services?.mainService?.sendCode ?? vm.$services.sendCode

      const sendCodeMethod = sendCodeResetPass ? vm.$services.authorization.repair.passwordResetSendCode : commonServiceMethod

      const response = await sendCodeMethod(payload)

      vm.form.fields.emailCode.sending = false
      if (response.success) {
        vm.form.fields.emailCode.codeIsSent = true
        vm.form.fields.emailCode.letRetry = false
        vm.form.fields.emailCode.retryTimeout = moment(response.data.repeatAfter).diff(moment(), 'seconds')
        vm.form.fields.emailCode.retries -= 1
        setTimeout(() => {
          vm.form.fields.emailCode.retryTimeout -= 1
        }, 1000)
      } else {
        if (response?.data?.repeatAfter) {
          vm.form.fields.emailCode.codeSentBefore = true
          vm.form.fields.emailCode.codeIsSent = true
          vm.form.fields.emailCode.letRetry = false
          vm.form.fields.emailCode.retryTimeout = moment(response.data.repeatAfter).diff(moment(), 'seconds')
          vm.form.fields.emailCode.retries -= 1
          setTimeout(() => {
            vm.form.fields.emailCode.retryTimeout -= 1
          }, 1000)
        } else {
          vm.form.fields.emailCode.codeSendError = true
          vm.form.fields.emailCode.letRetry = true
        }
      }
    }
  }
})
